import React from 'react';
import { LucideIcon } from 'lucide-react';
import { ChevronRight } from 'lucide-react';
import { ResponsiveCardValue } from '../../components/common/ResponsiveCardValue';

export interface DashboardCardProps {
  title: string;
  value: string;
  change?: string;
  icon: LucideIcon;
  accentTop: string;
  iconColor: string;
  onClick: () => void;
  hint?: string;
  dense?: boolean;
  /** @deprecated unused — kept for call-site compatibility */
  tint?: string;
}

export function DashboardCard({
  title,
  value,
  change,
  icon: Icon,
  accentTop,
  iconColor,
  onClick,
  hint,
  dense = false,
}: DashboardCardProps) {
  const showChange = !!change && change.trim() !== '' && change.trim() !== '—';
  const isNegative = (change ?? '').trim().startsWith('-');

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative w-full min-w-0 overflow-hidden rounded-xl border border-zinc-200/90 bg-white text-left shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-all duration-200 hover:-translate-y-0.5 hover:border-zinc-300 hover:shadow-[0_6px_20px_rgba(0,0,0,0.06)] focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/35 focus-visible:ring-offset-2 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:border-zinc-600 dark:focus-visible:ring-offset-zinc-950 ${accentTop}`}
    >
      <div className={`flex items-stretch ${dense ? 'min-h-[4.5rem]' : 'min-h-[5rem]'}`}>
        <div
          className={`flex shrink-0 flex-col items-center justify-center border-r border-zinc-100 bg-zinc-50/80 dark:border-zinc-700/80 dark:bg-zinc-800/50 ${
            dense ? 'w-10 py-3 sm:w-11' : 'w-11 py-3 sm:w-12 sm:py-4'
          }`}
        >
          <div className={`flex items-center justify-center rounded-lg bg-white p-1.5 shadow-sm ring-1 ring-zinc-200/60 dark:bg-zinc-900 dark:ring-zinc-600/60 ${dense ? 'h-7 w-7' : 'h-8 w-8 sm:h-9 sm:w-9'}`}>
            <Icon
              className={`${dense ? 'h-3.5 w-3.5' : 'h-4 w-4 sm:h-[18px] sm:w-[18px]'} ${iconColor} dark:brightness-110`}
            />
          </div>
        </div>
        <div className={`min-w-0 flex-1 ${dense ? 'px-2.5 py-2.5 pr-1.5' : 'px-3 py-3 pr-2 sm:px-3.5 sm:py-3.5 sm:pr-2'}`}>
          <p className="truncate text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-500 dark:text-zinc-400">
            {title}
          </p>
          <ResponsiveCardValue
            value={value}
            className="mt-0.5 font-semibold text-zinc-950 dark:text-zinc-50"
            scale="compact"
          />
          {showChange ? (
            <div className="mt-1 flex items-baseline gap-1.5">
              <span
                className={`text-[11px] font-semibold tabular-nums sm:text-xs ${
                  isNegative ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-700 dark:text-emerald-400'
                }`}
              >
                {change}
              </span>
              <span className="text-[10px] font-medium text-zinc-400 dark:text-zinc-500">vs prior</span>
            </div>
          ) : hint ? (
            <p className="mt-0.5 truncate text-[10px] font-medium leading-snug text-zinc-400 dark:text-zinc-500 sm:text-[11px]">
              {hint}
            </p>
          ) : null}
        </div>
        <div className="hidden shrink-0 items-center pr-2.5 sm:flex">
          <ChevronRight className="h-4 w-4 text-zinc-300 transition-transform group-hover:translate-x-0.5 group-hover:text-zinc-500 dark:text-zinc-600 dark:group-hover:text-zinc-400" />
        </div>
      </div>
    </button>
  );
}
